import React from 'react';
import colors from 'assets/theme/base/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { EditIconButton } from '../TriageListPage.styles';
import { RouteKeys } from 'models/RouteModels';
import { TriageProStatus, TriageXrayStatus } from 'models/AppointmentModels';
import {
    CompleteStatusSpan,
    ReadyStatusSpan,
    MissingStatusSpan,
    AnalyzingStatusSpan,
    AddButton,
    StatusContainer
} from './StatusCell.styles';

type Kind = 'PROS' | 'XRAYS';

interface StatusCellProps {
    appointment: any;
    kind: Kind;
    lgBreakpointBreached: boolean;
    navigateToBaseRouteWithAptmtDetails: (args: {
        routeKey: RouteKeys;
        appointmentId: string;
        appointmentDateISOString: string;
        addtlQueryParams?: { [key: string]: string };
    }) => void;
}

const StatusCell: React.FC<StatusCellProps> = ({
    appointment,
    kind,
    lgBreakpointBreached,
    navigateToBaseRouteWithAptmtDetails,
}) => {
    if (!appointment) return <span>-</span>;

    const routeKey = kind === 'PROS' ? RouteKeys.TRIAGE_SURVEY : RouteKeys.MATCH_XRAYS;

    const handleNavigation = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigateToBaseRouteWithAptmtDetails({
            routeKey,
            appointmentId: appointment.appointment_id,
            appointmentDateISOString: appointment.appointment_date,
        });
    };

    const renderWithEdit = (Component: React.ComponentType, text: string) => (
        <StatusContainer>
            <Component>{text}</Component>
            <EditIconButton onClick={handleNavigation}>
                <FontAwesomeIcon color={colors.secondary.main} icon={faPen} size='1x' />
            </EditIconButton>
        </StatusContainer>
    );

    // Determine status
    const summary = appointment?.exam_summary || appointment?.exams;

    if (kind === 'PROS') {
        const proStatus = appointment.pro_status;
        const hasPros = summary?.pros?.has_pros;

        if (proStatus === TriageProStatus.COMPLETED) {
            return renderWithEdit(CompleteStatusSpan, 'Complete');
        }

        if (hasPros === false || (proStatus === TriageProStatus.INCOMPLETE && hasPros !== true)) {
            return <AddButton onClick={handleNavigation}>+ Add</AddButton>;
        }

        return renderWithEdit(MissingStatusSpan, 'Missing');
    }

    // XRAYS logic
    const xrayStatus = appointment.xray_status;
    const hasXrays = summary?.xrays?.has_xrays;

    if (xrayStatus === TriageXrayStatus.ANALYZING) {
        return <AnalyzingStatusSpan>Analyzing...</AnalyzingStatusSpan>;
    }

    if (xrayStatus === TriageXrayStatus.COMPLETED) {
        return renderWithEdit(CompleteStatusSpan, 'Complete');
    }

    if (xrayStatus === TriageXrayStatus.READY_FOR_REVIEW) {
        return renderWithEdit(ReadyStatusSpan, 'Ready');
    }

    if (xrayStatus === TriageXrayStatus.NO_XRAYS || hasXrays === false) {
        return <AddButton onClick={handleNavigation}>+ Add</AddButton>;
    }

    return renderWithEdit(MissingStatusSpan, 'Missing');
};

export default StatusCell;


